import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ContactService } from './contact.service';

@Component({
  selector: 'app-contact-page',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
  <main class="page">
    <div class="container">
      <header class="head">
        <h1 class="h1">Contact</h1>
        <p class="lead">Prêt à transformer votre data ? Audit gratuit sous 48h.</p>
      </header>

      <div class="grid">
        <form class="card form" (ngSubmit)="submit()" #contactForm="ngForm">
          <label>
            <span>Nom</span>
            <input
              type="text"
              name="name"
              [(ngModel)]="form.name"
              placeholder="Votre nom"
              required
              minlength="2"
            />
          </label>

          <label>
            <span>Email</span>
            <input
              type="email"
              name="email"
              [(ngModel)]="form.email"
              placeholder="vous@entreprise.com"
              required
              email
            />
          </label>

          <label>
            <span>Entreprise (optionnel)</span>
            <input
              type="text"
              name="company"
              [(ngModel)]="form.company"
              placeholder="Nom de votre entreprise"
            />
          </label>

          <label class="span2">
            <span>Votre besoin</span>
            <textarea
              rows="5"
              name="message"
              [(ngModel)]="form.message"
              placeholder="Contexte, objectifs, délais…"
              required
              minlength="10"
            ></textarea>
          </label>

          <button class="btn primary" type="submit" [disabled]="loading || !isValid()">
            {{ loading ? 'Envoi…' : 'Envoyer' }}
          </button>

          <p class="success" *ngIf="success">
            Votre message a bien été envoyé. Nous revenons vers vous rapidement.
          </p>

          <p class="error" *ngIf="error">
            {{ error }}
          </p>
        </form>

        <aside class="card info">
          <h2 class="h2">Ce que vous obtenez</h2>
          <ul class="list">
            <li>Audit gratuit sous 48h</li>
            <li>Conseil personnalisé</li>
            <li>Plan d’action orienté résultats</li>
          </ul>

          <div class="note">
            <b>Tip</b> : ajoutez 2–3 exemples de rapports / datasets, on accélère le diagnostic.
          </div>
        </aside>
      </div>
    </div>
  </main>
  `,
  styles: [`
    .page{ padding: 44px 0 70px; }
    .lead{ color: var(--muted); font-weight: 750; }

    .grid{
      margin-top: 16px;
      display:grid;
      grid-template-columns: 1.1fr .9fr;
      gap: 14px;
      align-items:start;
    }

    .form{
      display:grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      align-items:start;
    }

    .card{
      border-radius: 26px;
      border: 1px solid rgba(255,255,255,.10);
      background: rgba(255,255,255,.03);
      padding: 18px;
      box-shadow: 0 30px 120px rgba(0,0,0,.45);
    }

    label{ display:block; }
    span{
      display:block;
      margin-bottom: 6px;
      color: rgba(235,240,255,.72);
      font-weight: 850;
    }

    input, textarea{
      width:100%;
      border-radius: 16px;
      border: 1px solid rgba(255,255,255,.12);
      background: rgba(255,255,255,.04);
      color: rgba(235,240,255,.90);
      padding: 12px 12px;
      outline:none;
    }

    input:focus, textarea:focus{
      border-color: rgba(0,229,168,.55);
      box-shadow: 0 0 0 4px rgba(0,229,168,.12);
    }

    .span2{ grid-column: 1 / -1; }

    .btn.primary[disabled]{
      opacity:.65;
      cursor:not-allowed;
    }

    .success{
      grid-column: 1 / -1;
      margin: 6px 0 0;
      color: #34d399;
      font-weight: 800;
    }

    .error{
      grid-column: 1 / -1;
      margin: 6px 0 0;
      color: #fda4af;
      font-weight: 800;
    }

    .list{
      margin: 10px 0 0;
      padding-left: 18px;
      color: var(--muted);
      font-weight: 750;
    }

    .list li{ margin: 8px 0; }

    .note{
      margin-top: 14px;
      border-radius: 18px;
      border: 1px solid rgba(255,255,255,.10);
      background: rgba(255,255,255,.04);
      padding: 12px;
      color: rgba(235,240,255,.72);
      font-weight: 750;
    }

    @media (max-width: 980px){
      .grid{ grid-template-columns: 1fr; }
      .form{ grid-template-columns: 1fr; }
      .span2{ grid-column: auto; }
    }

    :host-context(body[data-theme="light"]) .card{
      border: 1px solid rgba(15,23,42,.08);
      background: rgba(255,255,255,.96);
      box-shadow: 0 18px 50px rgba(15,23,42,.10);
    }

    :host-context(body[data-theme="light"]) span{
      color: #334155;
    }

    :host-context(body[data-theme="light"]) input,
    :host-context(body[data-theme="light"]) textarea{
      border: 1px solid rgba(15,23,42,.10);
      background: #f8fafc;
      color: #0f172a;
    }

    :host-context(body[data-theme="light"]) .note{
      border: 1px solid rgba(15,23,42,.08);
      background: #f8fafc;
      color: #475569;
    }
  `]
})
export class ContactPageComponent {
  private contactService = inject(ContactService);

  form = {
    name: '',
    email: '',
    company: '',
    message: ''
  };

  loading = false;
  success = false;
  error = '';

  isValid(): boolean {
    return (
        this.form.name.trim().length >= 2 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email.trim()) &&
        this.form.message.trim().length >= 10
    );
  }

  async submit(): Promise<void> {
    if (!this.isValid() || this.loading) return;

    this.loading = true;
    this.success = false;
    this.error = '';

    try {
      await this.contactService.sendMessage(this.form);
      this.success = true;
      this.form = {
        name: '',
        email: '',
        company: '',
        message: ''
      };
    } catch (err) {
      console.error(err);
      this.error = 'Impossible d’envoyer le message pour le moment.';
    } finally {
      this.loading = false;
    }
  }
}
